import React from 'react';
import { ArrowRight, FlaskConical, Lock, MessageSquare, Search } from 'lucide-react';
import { useShellUiActions } from '../../context/ShellUiContext';
import { useAuthActions } from '../../context/AuthContext';
import { isChatLocked, isMcpOnlyCustomer } from '../../utils/moduleAccess';

// Start-page entry points. The home chat composer used to sit here; since
// 2026-09-10 nobody chats on the start page itself. It has three doors, in
// the sidebar's order: Chat, Research and Build (Chat joined on 2026-10-08).
// Each door does exactly what the matching sidebar entry does (App.tsx):
// Chat opens Chat at /lookup; Research and Build open an EMPTY
// workspace via a topic-less blank draft (openTypedProjects) — /research/new
// or /build/new — with the backend project created lazily on the first
// message. Locked doors open the shared "Unlock" dialog, same as the
// sidebar and the module cards further down the page, except for an MCP-only
// customer: their doors open the connect page, where their plan works.

type EntryVariant = 'chat' | 'research' | 'build';

// Same badge as the module-card headers, so the two locked states read as
// one system.
const lockedBadgeClass =
    'text-[9px] font-semibold bg-surface-sunken text-fg-tertiary px-1 py-px rounded-full uppercase tracking-wider';

interface EntryDoorProps {
    variant: EntryVariant;
    title: string;
    blurb: string;
    /** The action line at the bottom of an unlocked card ("Start a chat"),
     *  so the card reads as something to click. */
    action: string;
    icon: React.ReactNode;
    locked: boolean;
    /** BAR-1528: required, like the module cards' `mcpOnly`, so a new door
     *  cannot ship with the generic locked line for an MCP-only customer. */
    mcpOnly: boolean;
    onOpen: () => void;
}

function EntryDoor({ title, blurb, action, icon, locked, mcpOnly, onOpen }: EntryDoorProps) {
    // All three doors take the navy primary (2026-10-17; Build was copper).
    const iconTile = 'bg-primary text-on-primary';
    const focusRing = 'focus-visible:shadow-bark-focus';
    const hoverBorder = 'hover:border-primary';
    // Hover tints the whole card, not only the shadow.
    const hoverTint = 'hover:bg-primary-tint';
    const actionText = 'text-primary';
    const surface = locked
        ? 'bg-bg-warm border-border-default hover:bg-surface-sunken'
        : `bg-surface border-border-strong shadow-bark-xs hover:shadow-bark-md ${hoverBorder} ${hoverTint}`;

    // Three doors share the 880px start column. The icon sits left of the
    // title at every width, which keeps the cards low.
    return (
        <button
            type="button"
            onClick={onOpen}
            className={`group flex-1 flex items-start gap-3 text-left rounded-xl border px-5 py-4 transition-all
                        focus-visible:outline-none ${focusRing} ${surface}`}
        >
            <span
                className={`shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg ${
                    locked ? 'bg-surface-sunken text-fg-tertiary' : iconTile
                }`}
            >
                {locked ? <Lock className="w-5 h-5" strokeWidth={1.5} aria-hidden="true" /> : icon}
            </span>
            <span className="min-w-0 flex-1 self-stretch flex flex-col gap-0.5">
                <span
                    className={`font-body text-[20px] font-semibold leading-tight flex items-center gap-2 ${
                        locked ? 'text-fg-tertiary' : 'text-fg'
                    }`}
                >
                    {title}
                    {locked && (
                        // Same as the module-card headers: badge hidden from AT,
                        // sr-only suffix so the name reads "Research — locked".
                        <>
                            <span aria-hidden="true" className={lockedBadgeClass}>Locked</span>
                            <span className="sr-only"> — locked</span>
                        </>
                    )}
                </span>
                <span className={`font-body text-[13px] leading-relaxed ${locked ? 'text-fg-tertiary' : 'text-fg-secondary'}`}>
                    {/* An MCP-only customer (every free signup) has no
                        organization to enable anything for; the door says
                        where their plan works, and opens the connect page. */}
                    {locked
                        ? (mcpOnly
                            ? 'Your BARK plan works inside Claude, ChatGPT and Codex. See how to connect BARK.'
                            : 'Not enabled for your organization. View access details.')
                        : blurb}
                </span>
                {/* label-md (DESIGN.md). Pinned to the card's bottom, so the
                    three action lines sit on one line side by side. */}
                {!locked && (
                    <span className={`pt-2 sm:mt-auto inline-flex items-center gap-1.5 font-body text-[13px] font-semibold ${actionText}`}>
                        {action}
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} aria-hidden="true" />
                    </span>
                )}
            </span>
        </button>
    );
}

export default function HomeEntryButtons() {
    const { beginBlankDraft, onOpenUnlockModule, navigateToView } = useShellUiActions();
    const { isModuleDisabled, usesChatFree } = useAuthActions();
    // Chat belongs to no single module, so it locks on the server's own
    // condition, like its sidebar entry: with every web module off the
    // orchestrator refuses its turns (BAR-1528).
    const chatLocked = isChatLocked(isModuleDisabled, usesChatFree);
    const researchLocked = isModuleDisabled('regulatory_research');
    const buildLocked = isModuleDisabled('build_artifact');
    const mcpOnly = isMcpOnlyCustomer(isModuleDisabled);
    // Defence in depth: `/` already sends an MCP-only account to /connect
    // before Home renders, so the `connect` arm runs only if that redirect is
    // ever skipped.
    const openLocked = (label: string) => (mcpOnly ? navigateToView('connect') : onOpenUnlockModule(label));

    return (
        <div role="group" aria-label="Start" className="flex flex-col sm:flex-row gap-4">
            <EntryDoor
                variant="chat"
                title="Chat"
                blurb="A quick answer with sources. No project needed."
                action="Start a chat"
                icon={<MessageSquare className="w-5 h-5" strokeWidth={1.5} aria-hidden="true" />}
                locked={chatLocked}
                mcpOnly={mcpOnly}
                onOpen={() => (chatLocked
                    ? openLocked('Chat')
                    : navigateToView('lookup'))}
            />
            <EntryDoor
                variant="research"
                title="Research"
                blurb="For in-depth research. Builds a project with findings and a cited report."
                action="Start research"
                icon={<Search className="w-5 h-5" strokeWidth={1.5} aria-hidden="true" />}
                locked={researchLocked}
                mcpOnly={mcpOnly}
                onOpen={() => (researchLocked
                    ? openLocked('Regulatory research')
                    : beginBlankDraft('research'))}
            />
            <EntryDoor
                variant="build"
                title="Build"
                blurb="Turn regulation into models, code and artifacts."
                action="Start a build"
                icon={<FlaskConical className="w-5 h-5" strokeWidth={1.5} aria-hidden="true" />}
                locked={buildLocked}
                mcpOnly={mcpOnly}
                onOpen={() => (buildLocked
                    ? openLocked('Build')
                    : beginBlankDraft('build'))}
            />
        </div>
    );
}
